import type { SemanticRow, SemanticValue } from './analytics';

/**
 * The four figures frame `64:2647` asks for that the semantic layer cannot be
 * asked for.
 *
 * `measures.ts` publishes 22 measures and `dimensions.ts` 17 dimensions, and
 * none of them is an average, an argmax, a share or a comparison. There is also
 * **no `week` or `month` bucket** — the temporal dimensions are exactly `date`,
 * `hour` and `dayOfWeek` — and `AnalyticsQueryDto` takes
 * `{measures, dimensions, filters, dateRange, limit}` with no `compareTo`. So:
 *
 *  - `Avg daily revenue` and `Peak sales day` are derived from the daily series
 *    the trend chart already fetches — no extra request;
 *  - `% Total` is a row over the window total, client-side;
 *  - `+12% vs last month` needs a **second round trip** over
 *    {@link previousRange}, and is labelled "previous period" rather than "last
 *    month" because a month is not a bucket this API has.
 *
 * All of it is pure so the arithmetic is testable without a server, and so the
 * cases that have no honest answer — no baseline, a zero or negative total —
 * return `null` instead of a plausible-looking number.
 */

export interface IsoRange {
  from: string;
  to: string;
}

/**
 * The window immediately before this one, of the same length and not
 * overlapping it.
 *
 * Equal-length rather than "the previous calendar month": with no `month`
 * dimension the server cannot align to a month boundary anyway, and comparing a
 * 21-day window against a 31-day one would report a fall that is only a
 * difference in width.
 */
export function previousRange(range: IsoRange): IsoRange {
  const from = new Date(range.from).getTime();
  const to = new Date(range.to).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return range;
  const span = to - from;
  const end = from - 1;
  return { from: new Date(end - span).toISOString(), to: new Date(end).toISOString() };
}

export interface SeriesStats {
  /** Buckets that carried a value. Not the number of calendar days in the range. */
  buckets: number;
  total: number;
  /** Mean **per bucket that traded**, so a closed day does not drag it down. */
  average: number | null;
  /** The largest bucket, or null when nothing traded. */
  peak: { key: SemanticValue; value: number } | null;
}

/**
 * Mean and argmax over one measure of a `{dimension, measure}` series.
 *
 * Ties go to the first row, which is the earliest bucket: the server orders a
 * temporal grouping ascending, and "the first day it peaked" is the more useful
 * of two equal answers.
 */
export function seriesStats(
  rows: readonly SemanticRow[],
  measure: string,
  dimension: string,
): SeriesStats {
  let total = 0;
  let buckets = 0;
  let peak: { key: SemanticValue; value: number } | null = null;

  for (const row of rows) {
    const raw = row[measure];
    if (raw === null || raw === undefined) continue;
    const value = Number(raw);
    if (!Number.isFinite(value)) continue;
    total += value;
    buckets += 1;
    if (!peak || value > peak.value) peak = { key: row[dimension] ?? null, value };
  }

  return { buckets, total, average: buckets ? total / buckets : null, peak };
}

/**
 * A row's share of the window.
 *
 * Null when the total is zero or negative: §7.4 keeps negative money
 * representable on purpose (the negated `DISCOUNT` charge, the split-offset
 * `ADJUSTMENT`), and "40% of minus two hundred riyals" is not a reading anyone
 * can act on. A dash says so; a number would not.
 */
export function shareOf(value: SemanticValue, total: number): number | null {
  if (value === null || value === undefined) return null;
  if (!Number.isFinite(total) || total <= 0) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric / total : null;
}

/**
 * Period-over-period change, as a ratio.
 *
 * Null when the baseline is missing or zero — a rise from nothing has no
 * percentage, and rendering `+∞%` or `+100%` would both be inventions. The
 * denominator is the magnitude of the baseline so a recovery from a negative
 * period reads as a rise rather than a fall.
 */
export function changeRatio(
  current: SemanticValue,
  previous: SemanticValue,
): number | null {
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined) return null;
  const now = Number(current);
  const before = Number(previous);
  if (!Number.isFinite(now) || !Number.isFinite(before) || before === 0) return null;
  return (now - before) / Math.abs(before);
}
